export const buildStockLevelParams = (warehouseCode, brandId) => {
  const numericBrandId = Number(brandId) || 0;
  const params = {
    brand_id: numericBrandId,
    warehouse_code: warehouseCode,
    limit: 1000,
  };
  return params;
};

/**
 * Shape the product_master catalog (/api/stock-levels/inventory-skus) into picker
 * options WITHOUT stock quantities.
 *
 * Why this exists: quantities come from the stock engine, which recomputes every SKU's
 * on-hand at a warehouse from raw movements (~8s, p50 measured 7.5s). Blocking the
 * dropdown on that meant the operator could not even TYPE for ~8 seconds after picking
 * a warehouse. Finding a SKU does not need a quantity — the availability gate at save
 * (allocateFIFOBatches) is what actually decides whether the transfer is allowed. So seed
 * the list from the catalog immediately, and let quantities decorate it when they land.
 *
 * AMENDED 2026-07-16: this used to say the gate "recomputes it fresh and authoritatively
 * regardless". It no longer does, and that matters to THIS file. createWarehouseTransfer
 * now reuses the very cache entry the call below warms — it reads warehouse-wide with no
 * sku and the SAME limit/offset, so both land on cache key
 * `${companyId}_${brandId}_${WAREHOUSE}_${limit}_${offset}`. Consequence: the `limit: 1000`
 * in buildStockLevelParams is load-bearing on the BACKEND too. It is mirrored by
 * PICKER_STOCK_PAGE_LIMIT in backend/warehouse-transfers/service.js — change one and you
 * must change the other, or the save path silently stops sharing this warm entry and goes
 * back to a ~12s engine run per line item (correct, just slow). The upside of the coupling:
 * the quantity shown here and the quantity the gate enforces are now the same number.
 *
 * `stockLoading` (distinct from `stockUnknown`, which means "no brand chosen, stock
 * cannot be resolved") tells the UI the number is still coming, not absent.
 */
export const catalogToSkuOptions = (list = []) =>
  (Array.isArray(list) ? list : [])
    .map(s => ({
      sku: s.primary_sku || '',
      product_name: s.product_name || '',
      image_url: '',
      thumbnail_url: '',
      total_available: null,
      batch_count: 0,
      stockLoading: true,
    }))
    .filter(o => o.sku)
    .sort((a, b) => a.sku.localeCompare(b.sku));

export const stockItemsToSkuOptions = (items = [], warehouseCode = '') => {
  const whCode = String(warehouseCode || '').toUpperCase();
  return (items || [])
    .map(item => {
      const warehouse = (item.warehouses || []).find(w =>
        String(w.warehouse_code || '').toUpperCase() === whCode
      );
      if (!warehouse) return null;
      const available = Number(warehouse.quantity ?? warehouse.current_quantity ?? 0) || 0;
      return {
        sku: item.primary_sku || '',
        product_name: item.product_name || '',
        image_url: item.image_url || '',
        thumbnail_url: item.thumbnail_url || '',
        total_available: available,
        batch_count: Number(warehouse.batch_count || 0) || 0,
      };
    })
    .filter(option => option && option.sku)
    .sort((a, b) => {
      const aOut = a.total_available <= 0;
      const bOut = b.total_available <= 0;
      if (aOut !== bOut) return aOut ? 1 : -1;
      return a.sku.localeCompare(b.sku);
    });
};

/**
 * Merge the stock-engine quantities INTO the full catalog list, rather than replacing
 * the catalog with in-stock-only rows.
 *
 * Why (2026-09-03, Rishabh): the picker seeds instantly from the catalog (every active
 * SKU) then, when the stock engine returns, it USED TO overwrite the list with
 * stockItemsToSkuOptions() — which only contains SKUs that have a warehouse entry with
 * stock. Effect: a zero-stock SKU appeared for ~8s then VANISHED. That blocked building a
 * draft transfer for a finished good still on an open manufacture PO (0 on hand yet).
 * Merging keeps every catalog SKU selectable, decorates the ones the engine knows with
 * real quantities, and shows the rest as 0 (still pickable). The availability gate that
 * matters is at SHIP (allocateFIFOBatches), not here.
 *
 * Ordering mirrors stockItemsToSkuOptions: in-stock first, then zero/unknown, then by sku.
 */
export const mergeCatalogWithStock = (catalog = [], stockItems = [], warehouseCode = '') => {
  const stockBySku = new Map();
  for (const o of stockItemsToSkuOptions(stockItems, warehouseCode)) {
    stockBySku.set(String(o.sku).toUpperCase(), o);
  }
  const seen = new Set();
  const out = [];
  for (const c of (Array.isArray(catalog) ? catalog : [])) {
    const key = String(c.sku || '').toUpperCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const s = stockBySku.get(key);
    // stockLoading:false — quantities have now arrived (0 is a real answer, not "loading").
    out.push(s
      ? { ...c, ...s, stockLoading: false }
      : { ...c, total_available: 0, batch_count: 0, stockLoading: false });
  }
  // Defensive: never DROP a SKU the engine reported just because the catalog seed missed it.
  for (const [key, s] of stockBySku) {
    if (!seen.has(key)) { seen.add(key); out.push(s); }
  }
  return out.sort((a, b) => {
    const aOut = (a.total_available || 0) <= 0;
    const bOut = (b.total_available || 0) <= 0;
    if (aOut !== bOut) return aOut ? 1 : -1;
    return String(a.sku).localeCompare(String(b.sku));
  });
};
